import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, HashRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { AuthProvider } from "@/hooks/useAuth";
import { ThemeProvider } from "next-themes";
import { queryClient } from "@/lib/queryClient";
import ScrollToTop from "./components/ScrollToTop";
import DashboardLayout from "@/components/DashboardLayout";
import PublicLayout from "@/layouts/PublicLayout";

// Lazy load pages for better performance
const Index = lazy(() => import("./pages/Index"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const MiniSiteSettings = lazy(() => import("./pages/MiniSiteSettings"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Domains = lazy(() => import("./pages/Domains"));
const Knowledge = lazy(() => import("./pages/Knowledge"));
const Connectors = lazy(() => import("@/pages/Connectors"));
const Leads = lazy(() => import("./pages/Leads"));
const ChatLogs = lazy(() => import("./pages/ChatLogs"));
const ConversationInsights = lazy(() => import("./pages/ConversationInsights"));
const LiveInbox = lazy(() => import("./pages/LiveInbox"));
const Embed = lazy(() => import("./pages/Embed"));
const Analytics = lazy(() => import("./pages/Analytics"));
const Products = lazy(() => import("./pages/Products"));
const Team = lazy(() => import("./pages/Team"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminEmail = lazy(() => import("./pages/admin/AdminEmail"));
const AdminPlans = lazy(() => import("./pages/admin/AdminPlans"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminInvoices = lazy(() => import("./pages/admin/AdminInvoices"));
const AdminVouchers = lazy(() => import("@/pages/admin/AdminVouchers"));
const AdminMonitoring = lazy(() => import("./pages/admin/AdminMonitoring"));
const Clients = lazy(() => import("./pages/Clients"));
const Upgrade = lazy(() => import("./pages/Upgrade"));
const Billing = lazy(() => import("./pages/Billing"));
const MyPlans = lazy(() => import("./pages/MyPlans"));
const ApiAccess = lazy(() => import("./pages/ApiAccess"));
const InvoiceDetail = lazy(() => import("./pages/invoices/InvoiceDetail"));
const NotFound = lazy(() => import("./pages/NotFound"));
const WidgetStandalone = lazy(() => import("./pages/WidgetStandalone"));
const WhiteLabel = lazy(() => import("./pages/WhiteLabel"));
const Features = lazy(() => import("./pages/Features"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Checkout = lazy(() => import("./pages/Checkout"));
const Agency = lazy(() => import("./pages/Agency"));
const Profile = lazy(() => import("./pages/Profile"));
import WordpressGuide from "./pages/WordpressGuide";
import WhatsappGuide from "./pages/WhatsappGuide";
import GoogleSheetsGuide from "./pages/GoogleSheetsGuide";
import RestApiGuide from "./pages/RestApiGuide";
import MysqlGuide from "./pages/MysqlGuide";
const DeveloperNotes = lazy(() => import("./pages/DeveloperNotes"));
const AdminHelp = lazy(() => import("./pages/AdminHelp"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));

const PageLoading = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
  </div>
);

const useHashRouter =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'cdn.chatbaze.id' || 
   window.location.hostname === 'embed.chatbaze.id' ||
   window.location.hash.startsWith('#/widget') ||
   (window.location.hash.startsWith('#/') && 
    !window.location.hostname.includes('app.chatbaze.id') && 
    !window.location.hostname.includes('chatbaze.id')));
const Router = useHashRouter ? HashRouter : BrowserRouter;

// RedirectHandler: JS fallback redirect for apex domain paths.
// Primary redirect is handled server-side in worker.ts.
// Only redirect based on isMainDomain — NOT FORCE_APP_SUBDOMAIN — to prevent
// dev-mode from accidentally redirecting to production.
const RedirectHandler = () => {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") return;

    const hostname = window.location.hostname;
    const isMainDomain = hostname === "chatbaze.id" || hostname === "www.chatbaze.id";
    const isAlreadyAppSubdomain = hostname === "app.chatbaze.id";
    
    // Check if the current path is a public guide path to avoid redirecting it to dashboard context
    const isPublicGuide = location.pathname.startsWith('/guide/') || location.hash.includes('/guide/');

    if (!isAlreadyAppSubdomain && isMainDomain && !isPublicGuide) {
      window.location.replace(`https://app.chatbaze.id${location.pathname}${location.search}${location.hash}`);
    }
  }, [location]);

  return null;
};

const isWidgetRoute = window.location.pathname.startsWith('/widget') || window.location.hash.startsWith('#/widget');

// Detect Landing Page Domain
const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
const isCoreDomain = ['chatbaze.id', 'www.chatbaze.id', 'app.chatbaze.id', 'cdn.chatbaze.id', 'embed.chatbaze.id', 'staging.chatbaze.id', 'localhost', '127.0.0.1', 'lvh.me'].includes(hostname) || hostname.includes('ngrok-free.app');
const isLocalSubdomain = hostname.endsWith('.localhost') || hostname.endsWith('.lvh.me');
const isLandingPage = !isCoreDomain || isLocalSubdomain;

const PublicLandingPage = lazy(() => import("@/pages/PublicLandingPage"));

const App = () => {
  if (isLandingPage && !isWidgetRoute) {
    return (
      <ThemeProvider
        attribute="class"
        defaultTheme="light"
        enableSystem={false}
        disableTransitionOnChange
        enableColorScheme={false}
        storageKey="cb-landing-page-theme"
      >
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <Suspense fallback={<PageLoading />}>
              <PublicLandingPage />
            </Suspense>
          </TooltipProvider>
        </QueryClientProvider>
      </ThemeProvider>
    );
  }

  return (
  <ThemeProvider
    attribute="class"
    defaultTheme="system"
    enableSystem
    disableTransitionOnChange
    enableColorScheme={false}
    storageKey={isWidgetRoute ? "cb-widget-theme" : "cb-dashboard-theme"}
  >
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <Router>
          <RedirectHandler />
          <ScrollToTop />
          <AuthProvider>
            <Suspense fallback={<PageLoading />}>
              <Routes>
                {/* Public guide routes - High priority, no login required */}
                <Route path="/guide/wordpress" element={<PublicLayout><WordpressGuide /></PublicLayout>} />
                <Route path="/guide/whatsapp" element={<PublicLayout><WhatsappGuide /></PublicLayout>} />
                <Route path="/guide/google-sheets" element={<PublicLayout><GoogleSheetsGuide /></PublicLayout>} />
                <Route path="/guide/rest-api" element={<PublicLayout><RestApiGuide /></PublicLayout>} />
                <Route path="/guide/mysql" element={<PublicLayout><MysqlGuide /></PublicLayout>} />

                {/* Legacy guide redirects - High priority */}
                <Route path="/dashboard/knowledge/wordpress-guide" element={<Navigate to="/guide/wordpress" replace />} />
                <Route path="/dashboard/knowledge/whatsapp-guide" element={<Navigate to="/guide/whatsapp" replace />} />
                <Route path="/dashboard/knowledge/google-sheets-guide" element={<Navigate to="/guide/google-sheets" replace />} />
                <Route path="/dashboard/knowledge/google-sheets" element={<Navigate to="/guide/google-sheets" replace />} />
                <Route path="/dashboard/knowledge/rest-api-guide" element={<Navigate to="/guide/rest-api" replace />} />
                <Route path="/dashboard/knowledge/mysql-guide" element={<Navigate to="/guide/mysql" replace />} />
                <Route path="/dashboard/knowledge/mysql-database-guide" element={<Navigate to="/guide/mysql" replace />} />
                <Route path="/dashboard/knowledge/rest-api" element={<Navigate to="/guide/rest-api" replace />} />
                <Route path="/dashboard/knowledge/mysql" element={<Navigate to="/guide/mysql" replace />} />
                <Route path="/dashboard/knowledge/wordpress" element={<Navigate to="/guide/wordpress" replace />} />
                <Route path="/dashboard/knowledge/whatsapp" element={<Navigate to="/guide/whatsapp" replace />} />

                <Route path="/" element={<Index />} />
                <Route path="/about-us" element={<AboutUs />} />
                <Route path="/features" element={<Features />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/agency" element={<Agency />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />

                {/* Public pages */}
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />

                {/* Routes with DashboardLayout (requires authentication) */}
                <Route element={<DashboardLayout />}>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/dashboard/profile" element={<Profile />} />
                  <Route path="/dashboard/domains" element={<Domains />} />
                  <Route path="/dashboard/knowledge" element={<Knowledge />} />
                  <Route path="/dashboard/embed" element={<Embed />} />
                  <Route path="/dashboard/connectors" element={<Connectors />} />
                  <Route path="/dashboard/leads" element={<Leads />} />
                  <Route path="/dashboard/chat-logs" element={<ChatLogs />} />
                  <Route path="/dashboard/insights" element={<ConversationInsights />} />
                  <Route path="/dashboard/chat-missed" element={<Navigate to="/dashboard/insights" replace />} />
                  <Route path="/dashboard/live-inbox" element={<LiveInbox />} />
                  <Route path="/dashboard/analytics" element={<Analytics />} />
                  <Route path="/dashboard/products" element={<Products />} />
                  <Route path="/dashboard/plans" element={<MyPlans />} />
                  <Route path="/dashboard/upgrade" element={<Upgrade />} />
                  <Route path="/dashboard/billing" element={<Billing />} />
                  <Route path="/dashboard/white-label" element={<WhiteLabel />} />
                  <Route path="/dashboard/team" element={<Team />} />
                  <Route path="/dashboard/clients" element={<Clients />} />
                  <Route path="/dashboard/developer-notes" element={<DeveloperNotes />} />
                  <Route path="/dashboard/api-access" element={<ApiAccess />} />
                  <Route path="/dashboard/mini-site" element={<MiniSiteSettings />} />
                  <Route path="/dashboard/invoice/:id" element={<InvoiceDetail />} />
                  <Route path="/dashboard/invoices/:id" element={<InvoiceDetail />} />
                  <Route path="/dashboard/help" element={<AdminHelp />} />
                  
                  {/* Internal guide routes are accessed via public /guide paths directly */}

                  {/* Admin Routes */}
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/admin/users" element={<AdminUsers />} />
                  <Route path="/admin/plans" element={<AdminPlans />} />
                  <Route path="/admin/invoices" element={<AdminInvoices />} />
                  <Route path="/admin/vouchers" element={<AdminVouchers />} />
                  <Route path="/admin/email" element={<AdminEmail />} />
                  <Route path="/admin/settings" element={<AdminSettings />} />
                  <Route path="/admin/monitoring" element={<AdminMonitoring />} />

                  {/* Internal Developer Routes */}
                  <Route path="/dev/noted" element={<DeveloperNotes />} />
                </Route>

                <Route path="/widget" element={<WidgetStandalone />} />
                
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </AuthProvider>
        </Router>
      </TooltipProvider>
    </QueryClientProvider>
  </ThemeProvider>
  );
};

export default App;
